/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect, useMemo } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useRenewDomainMutation } from 'api/domain'
import { setInvoice } from 'store/slices/billingSlice'
import { useAppDispatch } from 'store/hooks'
import { getBillingCycle } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    id: number,
    form: RefObject<FormikProps<{ promo_code: string, billing_cycle_id: number }>>,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function RenewDomainForm({ id, form, close }: Props) {
    const dispatch = useAppDispatch()
    const [renewDomain, { data: invoice, isSuccess, isError }] = useRenewDomainMutation()
    const { data } = domainAPI.endpoints.domainRenewal.useQueryState(id)
    const pricing = useMemo(() => {
        if (!data) {
            return []
        }

        return data.pricing.map(({ price, billing_cycle: { id, name } }) => ({
            value: id,
            label: `${price} ${getBillingCycle(name)}`
        }))
    }, [data])

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if ((isSuccess || isError) && form.current) {
            form.current.setSubmitting(false)
            form.current.resetForm()
        }

        if (isSuccess && invoice) {
            close()
            dispatch(setInvoice(invoice.id))
        }
    }, [form, invoice, isSuccess, isError])

    return (
        <Formik
            innerRef={form}
            enableReinitialize={true}
            initialValues={{
                promo_code: '',
                billing_cycle_id: pricing.length >= 1 ? pricing[0].value : 0
            }}
            onSubmit={({ promo_code, billing_cycle_id }) => {
                if (promo_code === '') {
                    renewDomain({ id, billing_cycle_id })
                } else {
                    renewDomain({ id, promo_code, billing_cycle_id })
                }
            }}
        >
            {({ isSubmitting }) => (
                <Form id={'renewDomainForm'}>
                    <Field
                        label={'Renewal Period'}
                        name={'billing_cycle_id'}
                        type={'select'}
                        disabled={isSubmitting}
                        list={pricing}
                        component={NXUIForm.SelectField}
                    />
                    <Field
                        label={'Promo Code'}
                        name={'promo_code'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                </Form>
            )}
        </Formik>
    )
}
